import React, { useState, useEffect } from 'react';
import './ViewApplicants.css';

function ViewApplicants({ apiBase, roleColor }) {
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterTestStatus, setFilterTestStatus] = useState('');
  const [levels, setLevels] = useState([]);
  const [locations, setLocations] = useState([]);
  const [sortBy, setSortBy] = useState('created_at');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadApplicants();
  }, [filterLevel, filterLocation, filterTestStatus, currentPage, searchTerm, sortBy]);

  const loadApplicants = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterLevel) params.append('level', filterLevel);
      if (filterLocation) params.append('location', filterLocation);
      if (filterTestStatus) params.append('test_status', filterTestStatus);
      if (searchTerm) params.append('search', searchTerm);
      if (sortBy) params.append('sort_by', sortBy);
      params.append('limit', pageSize);
      params.append('offset', (currentPage - 1) * pageSize);

      const response = await fetch(`${apiBase}/admissions/applicants?${params}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (data.ok && data.data) {
        setApplicants(data.data.applicants || []);
        setTotal(data.data.pagination?.total || 0);
        
        // Extract unique levels and locations
        if (data.data.applicants) {
          const uniqueLevels = [...new Set(data.data.applicants.map(a => a.level).filter(Boolean))];
          const uniqueLocations = [...new Set(data.data.applicants.map(a => a.location).filter(Boolean))];
          setLevels(uniqueLevels.sort());
          setLocations(uniqueLocations.sort());
        }
      }
    } catch (err) {
      console.error('Error loading applicants:', err);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  const getTestStatusBadge = (status) => {
    switch (status) {
      case 'passed':
        return { label: 'Lulus', color: '#10b981' };
      case 'failed':
        return { label: 'Tidak Lulus', color: '#dc3545' };
      case 'pending':
      default:
        return { label: 'Pending', color: '#f59e0b' };
    }
  };

  return (
    <div className="view-applicants">
      <div className="view-header">
        <h2>Daftar Pendaftar</h2>
        <div className="view-stats">
          <div className="stat-info">
            <span className="stat-label">Total Pendaftar:</span>
            <span className="stat-value">{total}</span>
          </div>
          <div className="stat-info">
            <span className="stat-label">Halaman:</span>
            <span className="stat-value">{currentPage} / {totalPages || 1}</span>
          </div>
        </div>
      </div>

      <div className="filters-section">
        <input
          type="text"
          placeholder="Cari nama atau email..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          className="filter-input"
        />
        
        <select
          value={filterLevel}
          onChange={(e) => {
            setFilterLevel(e.target.value);
            setCurrentPage(1);
          }}
          className="filter-select"
        >
          <option value="">Semua Jenjang</option>
          {levels.map(level => (
            <option key={level} value={level}>{level}</option>
          ))}
        </select>

        <select
          value={filterLocation}
          onChange={(e) => {
            setFilterLocation(e.target.value);
            setCurrentPage(1);
          }}
          className="filter-select"
        >
          <option value="">Semua Lokasi</option>
          {locations.map(location => (
            <option key={location} value={location}>{location}</option>
          ))}
        </select>

        <select
          value={filterTestStatus}
          onChange={(e) => {
            setFilterTestStatus(e.target.value);
            setCurrentPage(1);
          }}
          className="filter-select"
        >
          <option value="">Semua Status Test</option>
          <option value="pending">Pending</option>
          <option value="passed">Lulus</option>
          <option value="failed">Tidak Lulus</option>
        </select>

        <select
          value={sortBy}
          onChange={(e) => {
            setSortBy(e.target.value);
            setCurrentPage(1);
          }}
          className="filter-select"
        >
          <option value="created_at">Terbaru</option>
          <option value="name">Nama (A-Z)</option>
          <option value="level">Jenjang</option>
        </select>

        <button onClick={loadApplicants} className="btn-refresh">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="23 4 23 10 17 10"></polyline>
            <polyline points="1 20 1 14 7 14"></polyline>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36M20.49 15a9 9 0 0 1-14.85 3.36"></path>
          </svg>
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="loading-state">
          <svg className="spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <circle cx="12" cy="12" r="10" />
          </svg>
          <p>Memuat data pendaftar...</p>
        </div>
      ) : applicants.length === 0 ? (
        <div className="empty-state">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
          </svg>
          <p>Tidak ada pendaftar</p>
        </div>
      ) : (
        <div className="applicants-table-wrapper">
          <table className="applicants-table">
            <thead>
              <tr>
                <th>Nama</th>
                <th>Email</th>
                <th>Jenjang</th>
                <th>Lokasi</th>
                <th>Status Test</th>
                <th>Nilai</th>
                <th>Tanggal Mendaftar</th>
              </tr>
            </thead>
            <tbody>
              {applicants.map(applicant => {
                const testStatus = getTestStatusBadge(applicant.test_status);
                return (
                  <tr key={applicant.id}>
                    <td className="cell-name">{applicant.name}</td>
                    <td className="cell-email">{applicant.email}</td>
                    <td className="cell-level">{applicant.level || '-'}</td>
                    <td className="cell-location">{applicant.location || '-'}</td>
                    <td>
                      <span
                        className="status-badge"
                        style={{ background: `${testStatus.color}20`, color: testStatus.color }}
                      >
                        {testStatus.label}
                      </span>
                    </td>
                    <td className="cell-score">
                      {applicant.test_score ? (
                        <span className="score-value">{applicant.test_score}</span>
                      ) : (
                        <span className="score-empty">-</span>
                      )}
                    </td>
                    <td className="cell-date">
                      {new Date(applicant.created_at).toLocaleDateString('id-ID', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {total > pageSize && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="btn-page"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Sebelumnya
          </button>

          <div className="page-info">
            {applicants.length > 0 && (
              <span>
                Menampilkan {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, total)} dari {total}
              </span>
            )}
          </div>

          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="btn-page"
          >
            Berikutnya
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

export default ViewApplicants;
